import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, CheckCircle2, Loader, FileText, Trash2, Folder } from 'lucide-react';
import { apiUrl } from '../../utils/api';

interface KBFile {
  id: number;
  file_name: string;
  folder_name: string | null;
  file_size: number;
  status: string;
  chunk_count: number;
}

interface ManageDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeBaseName: string;
  knowledgeBaseId: string;
  onUploadSuccess?: () => void;
}

const ManageDataModal: React.FC<ManageDataModalProps> = ({ // Nudge to force rebuild
  isOpen,
  onClose,
  knowledgeBaseName,
  knowledgeBaseId,
  onUploadSuccess,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<KBFile[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: 'success' | 'error' | 'processing' }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Delete file handler
  const handleDeleteFile = async (fileId: number) => {
    try {
      const response = await fetch(apiUrl(`/api/knowledge-bases/${knowledgeBaseId}/file/${fileId}`), {
        method: 'DELETE',
      });
      if (response.ok) {
        setExistingFiles((prev) => prev.filter((file) => file.id !== fileId));
      } else {
        setError('Failed to delete file');
      }
    } catch (err) {
      setError('Failed to delete file');
      console.error('Delete file error:', err);
    }
  };

  const handleViewFile = (fileId: number) => {
    const viewUrl = apiUrl(`/api/knowledge-bases/${knowledgeBaseId}/file/${fileId}/view`);
    window.open(viewUrl, '_blank');
  };

  useEffect(() => {
    if (isOpen) {
      fetchExistingFiles();
    }
  }, [isOpen, knowledgeBaseId]);

  const fetchExistingFiles = async () => {
    setLoadingExisting(true);
    try {
      console.log('Fetching files for KB ID:', knowledgeBaseId);
      const response = await fetch(apiUrl(`/api/knowledge-bases/${knowledgeBaseId}/files`));
      let data = await response.json();
      console.log('Received files data:', data);

      if (response.ok) {
        // Handle both array and object with value property formats
        if (data && data.value && Array.isArray(data.value)) {
          data = data.value;
        }

        if (Array.isArray(data)) {
          setExistingFiles(data);
        } else if (data && typeof data === 'object') {
          // If it's a single object, wrap it in an array
          setExistingFiles([data]);
        } else {
          setExistingFiles([]);
        }
      } else {
        console.error('Failed to fetch existing files, response not ok:', data);
      }
    } catch (err) {
      console.error('Failed to fetch existing files error:', err);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
      setError(null);
    }
  };

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Store relative path in a custom property if needed, but for now we'll just use the file objects
      setFiles((prev) => [...prev, ...newFiles]);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prev) => [...prev, ...newFiles]);
      setError(null);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    files.forEach((file) => {
      // Append with webkitRelativePath if available to preserve folder structure
      const path = (file as any).webkitRelativePath || file.name;
      formData.append('files', file, path);
    });

    try {
      const response = await fetch(
        apiUrl(`/api/knowledge-bases/${knowledgeBaseId}/upload`),
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      // Update uploaded files status
      files.forEach((file) => {
        setUploadedFiles((prev) => ({
          ...prev,
          [file.name]: 'processing',
        }));
      });

      // Trigger background processing
      await fetch(apiUrl(`/api/knowledge-bases/${knowledgeBaseId}/process`), {
        method: 'POST',
      });

      // Clear files and show success for current batch
      setFiles([]);
      setSuccess(true);

      // Refresh the existing files list immediately
      fetchExistingFiles();

      // Call success callback to refresh parent data
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Reset success message after 3 seconds to allow more uploads
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Manage Data</h2>
            <p className="text-sm text-slate-500 mt-1">{knowledgeBaseName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        {/* Content */}
        {success ? (
          <div className="px-6 py-12 flex flex-col items-center justify-center space-y-4">
            <CheckCircle2 className="text-emerald-600" size={48} />
            <div className="text-center">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Upload Successful! ✓</h3>
              <p className="text-slate-600">
                Your files have been uploaded and are being processed. <br />
                <span className="text-sm text-slate-500">Text extraction, chunking, and embeddings will start automatically.</span>
              </p>
            </div>
            <div className="pt-2">
              <p className="text-sm text-slate-500 italic">You can upload more files or close this dialog when done.</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Upload Zone */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors group"
            >
              <div className="flex justify-center gap-8 mb-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 group/file"
                  type="button"
                >
                  <div className="p-4 bg-blue-50 rounded-2xl group-hover/file:bg-blue-100 transition-colors">
                    <Upload className="text-blue-600" size={32} />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">Upload File</span>
                </button>
                <button
                  onClick={() => folderInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 group/folder"
                  type="button"
                >
                  <div className="p-4 bg-blue-50 rounded-2xl group-hover/folder:bg-blue-100 transition-colors">
                    <Folder className="text-blue-600" size={32} />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">Upload Folder</span>
                </button>
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Drop files here or click above icons</h3>
              <p className="text-sm text-slate-500">PDF, DOCX, TXT and other document formats supported</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.docx,.txt,.doc,.xlsx,.csv"
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                {...({
                  webkitdirectory: "",
                  directory: "",
                } as any)}
                onChange={handleFolderSelect}
                className="hidden"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-red-900">Upload Error</h4>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* File List */}
            {files.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-900">
                  Selected Files ({files.length})
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-slate-50 p-3 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-xs font-bold text-blue-600">
                            {file.name.split('.').pop()?.toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      {uploading ? (
                        uploadedFiles[file.name] === 'success' ? (
                          <CheckCircle2 className="text-emerald-600 flex-shrink-0" size={20} />
                        ) : uploadedFiles[file.name] === 'error' ? (
                          <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                        ) : (
                          <Loader className="text-blue-600 flex-shrink-0 animate-spin" size={20} />
                        )
                      ) : (
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-slate-200 rounded transition-colors"
                        >
                          <X size={16} className="text-slate-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Files Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">
                Files in Knowledge Base
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-slate-100 rounded-xl p-2">
                {loadingExisting ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="text-blue-600 animate-spin" size={24} />
                  </div>
                ) : existingFiles.length > 0 ? (
                  (() => {
                    const grouped: { [key: string]: KBFile[] } = {};
                    const standalone: KBFile[] = [];

                    existingFiles.forEach(file => {
                      if (file.folder_name) {
                        if (!grouped[file.folder_name]) grouped[file.folder_name] = [];
                        grouped[file.folder_name].push(file);
                      } else {
                        standalone.push(file);
                      }
                    });

                    return (
                      <>
                        {Object.entries(grouped).map(([folderName, folderFiles]) => (
                          <div key={folderName} className="bg-slate-50 p-3 rounded-lg border border-slate-100 mb-2">
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-200">
                              <div className="flex items-center gap-3">
                                <Folder size={18} className="text-blue-500" />
                                <span className="text-sm font-bold text-slate-900">{folderName}</span>
                                <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-black uppercase">Folder • {folderFiles.length} items</span>
                              </div>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete the entire folder "${folderName}"?`)) {
                                    folderFiles.forEach(f => handleDeleteFile(f.id));
                                  }
                                }}
                                className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded transition-colors"
                                title="Delete folder"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="space-y-1 pl-7 opacity-60">
                              {folderFiles.map(f => (
                                <div className="flex items-center justify-between group/file">
                                  <div
                                    className="text-[11px] text-slate-600 truncate hover:text-blue-600 hover:underline cursor-pointer transition-colors flex-1"
                                    onClick={() => handleViewFile(f.id)}
                                    title="Click to view file"
                                  >
                                    {f.file_name.replace(`${folderName}/`, '')}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Are you sure you want to delete "${f.file_name}"?`)) {
                                        handleDeleteFile(f.id);
                                      }
                                    }}
                                    className="p-0.5 opacity-0 group-hover/file:opacity-100 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded transition-all ml-2"
                                    title="Delete file"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {standalone.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group/item cursor-pointer"
                            onClick={() => handleViewFile(file.id)}
                            title="Click to view file"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex-shrink-0 w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center group-hover/item:bg-blue-100 transition-colors">
                                <FileText size={16} className="text-slate-600 group-hover/item:text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate group-hover/item:text-blue-700">
                                  {file.file_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {(file.file_size / 1024 / 1024).toFixed(2)} MB • {file.status}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFile(file.id);
                              }}
                              className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-colors"
                              title="Delete file"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </>
                    );
                  })()
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No files uploaded yet.
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 rounded-lg border border-slate-300 font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 transition-colors"
          >
            {success ? 'Done' : 'Cancel'}
          </button>
          {!success && (
            <>
              <button
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                className="px-4 py-2 rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload {files.length > 0 ? `(${files.length})` : ''}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageDataModal;
